import React, { useState } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { Formik, Form } from "formik";
import * as Yup from "yup";

const ManagementDetailsModal = ({
  onClose,
  settingsData,
  addManagementDetailMutate,
  isManagementDetailLoading,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    mobile: Yup.string()
      .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
      .required("Mobile is required"),
    businessName: Yup.string().required("Business name is required"),
    gstNumber: Yup.string()
      .matches(/^[0-9A-Z]{15}$/, "Invalid GST number")
      .required("GST number is required"),
  });

  return (
    <Container>
      <div className="flex items-center justify-between bg-[#0078BD] py-6 px-4">
        <Title className="text-white">
          {isEditing ? "Edit Management Details" : "Management Details"}
        </Title>
        <button onClick={onClose}>
          <MdClose color="white" size={24} />
        </button>
      </div>

      <Formik
        enableReinitialize
        initialValues={{
          name: settingsData?.name || "",
          email: settingsData?.email || "",
          mobile: settingsData?.mobile || "",
          businessName: settingsData?.businessName || "",
          gstNumber: settingsData?.gstNumber || "",
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          addManagementDetailMutate(values, {
            onSuccess: () => {
              setIsEditing(false);
            },
          });
        }}
      >
        {({ values, handleChange, errors, touched }) =>
          isEditing ? (
            <Form className="px-6 h-full flex flex-col justify-between py-4 relative">
              <Wrapper>
                <InputGroup>
                  <Label>Name</Label>
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                  />
                  {touched.name && errors.name && <Error>{errors.name}</Error>}
                </InputGroup>

                <InputGroup>
                  <Label>Email</Label>
                  <Input
                    id="email"
                    type="text"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                  />
                  {touched.email && errors.email && (
                    <Error>{errors.email}</Error>
                  )}
                </InputGroup>

                <InputGroup>
                  <Label>Mobile</Label>
                  <Input
                    id="mobile"
                    type="text"
                    name="mobile"
                    value={values.mobile}
                    onChange={handleChange}
                  />
                  {touched.mobile && errors.mobile && (
                    <Error>{errors.mobile}</Error>
                  )}
                </InputGroup>

                <InputGroup>
                  <Label>Business Name</Label>
                  <Input
                    id="businessName"
                    type="text"
                    name="businessName"
                    value={values.businessName}
                    onChange={handleChange}
                  />
                  {touched.businessName && errors.businessName && (
                    <Error>{errors.businessName}</Error>
                  )}
                </InputGroup>

                <InputGroup>
                  <Label>GST No.</Label>
                  <Input
                    id="gstNumber"
                    type="text"
                    name="gstNumber"
                    value={values.gstNumber}
                    onChange={handleChange}
                  />
                  {touched.gstNumber && errors.gstNumber && (
                    <Error>{errors.gstNumber}</Error>
                  )}
                </InputGroup>
              </Wrapper>

              <BtnWrapper>
                <SecondaryButton onClick={onClose} disabled={isManagementDetailLoading}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={isManagementDetailLoading}>
                  {isManagementDetailLoading ? "Updating..." : "Update"}
                </PrimaryButton>
              </BtnWrapper>
            </Form>
          ) : (
            <div className="px-6 h-full flex flex-col justify-between py-4 relative">
              <Wrapper>
                <SectionTitle>Management & Company Details</SectionTitle>
                <Row>
                  <Label>Name</Label>
                  <Value>{settingsData?.name || "Kipinn"}</Value>
                </Row>
                <Row>
                  <Label>Email</Label>
                  <Value>{settingsData?.email || ""}</Value>
                </Row>
                <Row>
                  <Label>Mobile</Label>
                  <Value>{settingsData?.mobile || "0000222233"}</Value>
                </Row>
                <Row>
                  <Label>Business Name</Label>
                  <Value>{settingsData?.businessName || "Kipinn Business"}</Value>
                </Row>
                <Row>
                  <Label>GST No.</Label>
                  <Value>{settingsData?.gstNumber || ""}</Value>
                </Row>
              </Wrapper>

              <BtnWrapper>
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <PrimaryButton onClick={() => setIsEditing(true)}>Edit</PrimaryButton>
              </BtnWrapper>
            </div>
          )
        }
      </Formik>
    </Container>
  );
};

const Wrapper = tw.div`grid gap-2 my-2`;
const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const Title = tw.h1`text-lg md:text-xl font-medium text-center text-gray-800`;
const SectionTitle = tw.h2`text-lg font-semibold text-gray-700`;
const Row = tw.div`flex justify-between py-2 md:p-2`;
const Label = tw.label`text-sm text-gray-500 font-normal`;
const Value = tw.span`text-gray-800 font-medium`;
const InputGroup = tw.div`flex flex-col space-y-1.5 mb-1`;
const Input = tw.input`border border-gray-300 rounded h-10 px-2 py-1 focus:ring-1 focus:ring-primary-color focus:border-primary-color focus:outline-none`;
const Error = tw.p`text-rose-600 text-sm`;

const BtnWrapper = tw.div`${({ $isEditing }) =>
  $isEditing
    ? "flex gap-3"
    : "flex flex-col sm:flex-row gap-3 w-full sm:justify-between mt-4"}`;

const PrimaryButton = tw.button`${({ $isEditing }) =>
  $isEditing
    ? "bg-primary-color w-1/2 text-white px-4 py-2 rounded-lg hover:bg-[#65adf0]"
    : "bg-primary-color w-full sm:w-1/2 text-white px-4 py-2 rounded-lg hover:bg-[#65adf0]"}`;

const SecondaryButton = tw.button`${({ $isEditing }) =>
  $isEditing
    ? "bg-white border w-1/2 border-primary-color text-primary-color font-medium px-4 py-1 rounded-lg"
    : "bg-white border w-full sm:w-1/2 border-primary-color text-primary-color font-medium px-4 py-1 rounded-lg"}`;
export default ManagementDetailsModal;
